/**
 * CycleActivityLog
 *
 * Fetches and displays all governance_events for a given cycle via the
 * get_cycle_audit_trail RPC. Rendered in the Cycle History tab alongside
 * the existing structural LineageTimeline.
 *
 * Each row shows:
 *   – Typed icon + event label
 *   – Actor (resolved to display name when possible)
 *   – Timestamp
 *   – Optional note / state-change detail
 *   – If item_id is set: a link to that specific item
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2,
  FileEdit,
  CheckCircle,
  AlertCircle,
  Lock,
  Unlock,
  Layers,
  BarChart3,
  ArrowRight,
  Circle,
  Upload,
  Archive,
  Users,
  Calendar,
} from 'lucide-react';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { itemInContextHref } from '@/lib/itemCycleStanding';

// ============================================================================
// Event type config
// ============================================================================

const CYCLE_EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string; bgColor: string; label: string }> = {
  ranking_completed:           { icon: BarChart3,   color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]',  label: 'Ranking Completed' },
  ranking_overridden:          { icon: AlertCircle, color: 'text-[var(--mustard)]',  bgColor: 'bg-[var(--mustard-dim)]',  label: 'Ranking Overridden' },
  criteria_updated:            { icon: FileEdit,    color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]',    label: 'Criteria Updated' },
  criteria_changed:            { icon: FileEdit,    color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]',    label: 'Criteria Changed' },
  criteria_locked:             { icon: Lock,        color: 'text-[var(--text-sec)]',   bgColor: 'bg-[var(--hover-tint)]',  label: 'Criteria Locked' },
  criteria_unlocked:           { icon: Unlock,      color: 'text-[var(--text-ter)]',   bgColor: 'bg-[var(--surface)]',   label: 'Criteria Unlocked' },
  candidate_pool_updated:      { icon: Layers,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]',    label: 'Pool Updated' },
  pool_modified:               { icon: Layers,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]',    label: 'Pool Modified' },
  ItemPoolLocked:              { icon: Lock,        color: 'text-[var(--text-sec)]',   bgColor: 'bg-[var(--hover-tint)]',  label: 'Pool Locked' },
  ItemPoolUnlocked:            { icon: Unlock,      color: 'text-[var(--text-ter)]',   bgColor: 'bg-[var(--surface)]',   label: 'Pool Unlocked' },
  ItemPoolCreated:             { icon: Layers,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]',    label: 'Pool Created' },
  value_convergence_completed: { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]', label: 'Values Converged' },
  value_source_selected:       { icon: FileEdit,    color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]',  label: 'Value Source Selected' },
  PublishCompleted:            { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]', label: 'Published' },
  PublishAttempted:            { icon: Upload,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]',    label: 'Publish Attempted' },
  FocusListArchived:           { icon: Archive,     color: 'text-[var(--mustard)]',   bgColor: 'bg-[var(--mustard-dim)]',   label: 'Focus List Archived' },
  session_created:             { icon: Users,       color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]',    label: 'Session Created' },
  cycle_published:             { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]', label: 'Cycle Published' },
};

const DEFAULT_CONFIG = { icon: Circle, color: 'text-[var(--text-ter)]', bgColor: 'bg-[var(--hover-tint)]', label: '' };

function humanize(eventType: string): string {
  return eventType
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

// ============================================================================
// Types
// ============================================================================

interface CycleGovEvent {
  id: string;
  event_type: string;
  occurred_at: string;
  actor_id: string | null;
  actor_name: string | null;
  note: string | null;
  from_state: string | null;
  to_state: string | null;
  payload: Record<string, unknown> | null;
  item_id: string | null;
}

// ============================================================================
// Component
// ============================================================================

export interface CycleActivityLogProps {
  cycleId: string;
  /** Maximum events to display (undefined = all) */
  maxEvents?: number;
  className?: string;
}

export function CycleActivityLog({ cycleId, maxEvents, className = '' }: CycleActivityLogProps) {
  const [events, setEvents] = useState<CycleGovEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    supabase
      .rpc('get_cycle_audit_trail', { p_cycle_id: cycleId })
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) {
          console.warn('[CycleActivityLog] RPC failed:', rpcError.message);
          setError(rpcError.message);
        } else {
          const rows = (data ?? []) as CycleGovEvent[];
          setEvents(maxEvents != null ? rows.slice(0, maxEvents) : rows);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [cycleId, maxEvents]);

  // Collect actor UUIDs for display-name resolution
  const actorIds = useMemo(
    () => events.map((e) => e.actor_id).filter((id): id is string => id != null),
    [events]
  );
  const actorDisplayMap = useActorDisplayMap(actorIds);
  const getItemById = useItemsStore((s) => s.getItemById);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 py-8 text-[var(--text-ter)] text-sm ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading activity…
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 py-4 text-[var(--coral)] text-sm ${className}`}>
        <AlertCircle className="h-4 w-4" />
        Failed to load cycle activity: {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={`py-8 text-center text-[var(--text-ter)] text-sm ${className}`}>
        No governance events recorded for this cycle yet.
        <p className="mt-1 text-xs text-[var(--text-ter)]">Events appear as you lock the pool, run sessions, and publish results.</p>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden ${className}`}>
      <table className="w-full text-left text-sm">
        <colgroup>
          <col className="w-auto" />
          <col className="w-[120px]" />
          <col className="w-[120px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-[var(--rule)]">
            <th className="pb-2 pl-4 pr-3 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)]">Event</th>
            <th className="pb-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)]">Actor</th>
            <th className="pb-2 px-3 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)]">When</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => {
            const cfg = CYCLE_EVENT_CONFIG[event.event_type] ?? DEFAULT_CONFIG;
            const Icon = cfg.icon;
            const label = cfg.label || humanize(event.event_type);
            const actor = event.actor_id ? (actorDisplayMap[event.actor_id] ?? event.actor_name ?? event.actor_id.slice(0, 8) + '…') : '—';

            const tsDate = new Date(event.occurred_at);
            const shortDate = Number.isNaN(tsDate.getTime())
              ? '—'
              : new Intl.DateTimeFormat('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                }).format(tsDate);

            // Parse note: sometimes stored as JSON string
            let noteText: string | null = event.note;
            if (noteText) {
              try {
                const parsed = JSON.parse(noteText);
                if (parsed && typeof parsed === 'object') {
                  noteText = null; // suppress raw JSON — payload covers it
                }
              } catch { /* not JSON, keep as-is */ }
            }

            // Item link when the event is scoped to a specific item
            const linkedItem = event.item_id ? getItemById(event.item_id) : null;

            return (
              <tr key={event.id} className="group border-b border-[var(--rule)] last:border-0 hover:bg-[var(--surface)]/60 transition-colors">
                <td className="py-2.5 pl-4 pr-3 align-top">
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full ${cfg.bgColor} flex items-center justify-center`}>
                      <Icon className={`h-2.5 w-2.5 ${cfg.color}`} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-[var(--text-pri)] leading-snug">{label}</p>
                      {(noteText || event.from_state || event.to_state || linkedItem) && (
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--text-ter)]">
                          {event.from_state && event.to_state && (
                            <span className="flex items-center gap-0.5">
                              {event.from_state}
                              <ArrowRight className="h-2.5 w-2.5 text-[var(--text-ter)]" />
                              <span className="text-[var(--text-sec)]">{event.to_state}</span>
                            </span>
                          )}
                          {noteText && <span className="text-[var(--text-ter)] truncate max-w-[280px]">{noteText}</span>}
                          {linkedItem && (
                            <Link
                              to={itemInContextHref(linkedItem.id, { cycleId })}
                              className="inline-flex items-center gap-0.5 text-[var(--text-ter)] hover:underline"
                            >
                              <Calendar className="h-2.5 w-2.5" />
                              {linkedItem.title
                                ? `"${linkedItem.title.slice(0, 40)}${linkedItem.title.length > 40 ? '…' : ''}"`
                                : 'View item'}
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-2.5 px-3 align-top whitespace-nowrap text-xs text-[var(--text-ter)] max-w-[120px] truncate">
                  {actor}
                </td>
                <td className="py-2.5 px-3 align-top whitespace-nowrap text-xs text-[var(--text-ter)]">
                  {shortDate}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
